<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import { useAlert } from 'dashboard/composables';
import SeletorDeEtiquetas from 'dashboard/components-next/label/SeletorDeEtiquetas.vue';
import ChipDeEtiqueta from 'dashboard/components-next/label/ChipDeEtiqueta.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    default: null,
  },
});

const { t } = useI18n();

const store = useStore();
const route = useRoute();

const allLabels = useMapGetter('labels/getLabels');
const contactLabels = useMapGetter('contactLabels/getContactLabels');

// A Label manual "cliente" (pré-existente, frágil) é escondida da lista
// normal de etiquetas — o badge fixo abaixo (isLegalClient) já cobre esse
// sinal, com fonte de verdade real (LegalClient), não uma tag solta.
const savedLabels = computed(() => {
  const availableContactLabels = contactLabels.value(props.contactId);
  return allLabels.value.filter(
    ({ title }) =>
      availableContactLabels.includes(title) &&
      title.toLowerCase() !== 'cliente'
  );
});

// A etiqueta "cliente" (manual, frágil) não se oferece: o selo estrutural
// de cliente já cobre esse sinal.
const etiquetasOferecidas = computed(() =>
  (allLabels.value || []).filter(
    ({ title }) => title.toLowerCase() !== 'cliente'
  )
);
const titulosEscolhidos = computed(() => savedLabels.value.map(l => l.title));

const falhouCarga = ref(false);

const fetchLabels = async contactId => {
  if (!contactId) {
    return;
  }
  falhouCarga.value = false;
  try {
    await store.dispatch('contactLabels/get', contactId);
  } catch {
    falhouCarga.value = true;
  }
};

// Liga ou desliga a etiqueta. A falha era engolida ("// error") — quem
// clicava não sabia que a etiqueta não tinha sido salva.
// A base é a lista INTEIRA do servidor, com a "cliente" escondida: o
// servidor troca a lista toda, então partir da lista filtrada apagava a
// "cliente" a cada etiqueta adicionada (16/09/2026).
const alternarEtiqueta = async etiqueta => {
  const atuais = contactLabels.value(props.contactId) || [];
  const labels = atuais.includes(etiqueta.title)
    ? atuais.filter(titulo => titulo !== etiqueta.title)
    : [...atuais, etiqueta.title];
  try {
    await store.dispatch('contactLabels/update', {
      contactId: props.contactId,
      labels,
    });
  } catch {
    useAlert(t('LABEL.SAVE_FAILED'));
  }
};

watch(
  () => props.contactId,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      fetchLabels(newVal);
    }
  }
);
onMounted(() => {
  // route.params.contactId na página de Contatos; props.contactId dentro da conversa.
  fetchLabels(route.params.contactId || props.contactId);
});
</script>

<template>
  <div class="cel">
    <!-- Etiqueta removida encolhe e as vizinhas deslizam para o lugar (24/10/2026). -->
    <TransitionGroup name="cde">
      <ChipDeEtiqueta
        v-for="label in savedLabels"
        :key="label.id"
        :etiqueta="label"
        @remover="alternarEtiqueta"
      />
    </TransitionGroup>
    <span v-if="falhouCarga" class="advup-nota advup-nota--erro cel__vazio">
      {{ t('LABEL.LOAD_FAILED') }}
    </span>
    <span v-else-if="!savedLabels.length" class="advup-nota cel__vazio">
      {{ t('LABEL.EMPTY') }}
    </span>
    <SeletorDeEtiquetas
      :etiquetas="etiquetasOferecidas"
      :escolhidas="titulosEscolhidos"
      :rotulo="t('CONTACT_PANEL.CONTACT_LABELS')"
      @alternar="alternarEtiqueta"
    />
  </div>
</template>

<style scoped>
.cel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--esp-1);
}
</style>
